import { from, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';

import {
  AccountBalanceInfoResult,
  AccountBalanceResult,
  AccountCentralizedInfo,
  AccountInfoResult,
  AccountPendingUndelegation,
  ActivityExportDeployStatusesResult,
  ActivityExportEntitlementNFTEntryType,
  ActivityExportRecentRequest,
  ActivityExportRequestResult,
  ActivityExportRequestType,
  AllocationHistoryResult,
  AssociatedKeyRelatedToAccount,
  BlockResult,
  ContractPackageVersion,
  ContractResult,
  CsprNameInfoResult,
  CurrencyFiatResult,
  CurrencyRateResult,
  DailyDeployNumberResult,
  DelegatorRewardByEraResult,
  DelegatorResult,
  DelegatorRewardResult,
  DeployContractPackageResult,
  DeployEntryPointResult,
  DeployResult,
  FriendlyMarketTokenPairResult,
  FTBalanceTokenResult,
  FTTokenOwnerResult,
  FTTokenRatesHistoryResult,
  FTTransactionResult,
  GetDeployResult,
  MapDailyDeployNumberResult,
  MapDataResponseValidators,
  MapMetadata,
  MapPaginatedBlock,
  MapPaginatedBlocks,
  MapPaginatedDeploy,
  MapPaginatedDeploys,
  MapPaginatedValidators,
  MapTokensMetadataAsList,
  NetworkStats,
  NFTTokenActivityResult,
  NFTTokenMetadataEntry,
  NFTTokenOwnerResult,
  NFTTokenResult,
  RawDeployJSON,
  RawTransactionJSON,
  Response,
  StatusResult,
  SupplyResult,
  TotalRewardsResult,
  TransferResult,
  URefDelegationResult,
  ValidatorMetricsResult,
  ValidatorResult,
  ValidatorRewardResult,
  ValidatorsUnstakingBalanceResult,
  ContractEntryPointCostsResult,
} from '@block-explorer/entities';
import { OrderDirection } from '@block-explorer/ui';
import { isObjEmpty } from '../utils/object';

export type DataResponse<T = any> = {
  data: T;
  error?: { message: string };
};

export type RpcResponse<T = any> = {
  result: T;
  error?: { code: number; message: string };
};

export type PaginatedResponse<T = any> = {
  data: T[];
  item_count: number;
  page_count: number;
};

export type RequestParams = Record<
  string,
  string | boolean | string[] | number | null | undefined
>;

export type QueryRequestParams = RequestParams | undefined;

export type PostRequestParams = Record<
  string,
  | Record<string, string | number>
  | string
  | boolean
  | string[]
  | number
  | number[]
  | null
  | undefined
>;

export type BatchGetRequestParams = boolean[] | string[] | number[];

export type BatchGetRequestParam = { batch_id: BatchGetRequestParams };

export type PaginationParams<T = any> = {
  page: number;
  page_size: number;
  order_by: string | undefined;
  order_direction: OrderDirection;
};

export const fromFetchResponseHandler: OperatorFunction<any, any> = switchMap(
  (res) => {
    return res.json();
  }
);

export const fetchResponseHandler = (res) => {
  return res.json();
};

export const globalErrorHandler = catchError((error) => {
  console.error(error);
  return of(error);
});

const RPC_URL = config.cspr_rpc_url;
const SPECULATIVE_EXEC_RPC_URL = config.cspr_speculative_exec_rpc_url;
const CSPR_LIVE_API_URL = config.cspr_live_api_url;

const replacePaginatedParams = (url: string, params: RequestParams): string => {
  const urlHasQuery = url.includes('?');
  const concatSign = urlHasQuery ? '&' : '?';
  const pageLimitQuery = 'page_size=:page_size';
  if (!isObjEmpty(params)) delete params.defaultPageSize;

  return replaceParams(
    `${url}${concatSign}page=:page&${pageLimitQuery}&order_by=:order_by&order_direction=:order_direction`,
    params
  );
};

const replaceParams = (url: string, params: RequestParams): string => {
  return Object.entries(params).reduce((_url, [key, value]) => {
    // append new url param when url param key not in the URL
    if (!_url.includes(key)) {
      const urlHasQuery = _url.includes('?');
      const concatSign = urlHasQuery ? '&' : '?';

      return _url.concat(`${concatSign}${key}=${value}`);
    }

    // remove placeholder when value null
    if (value == null) {
      _url = _url.replace(`&${key}=:${key}`, '');
      return _url.replace(`?${key}=:${key}`, '');
    }

    // replace placeholder with value
    return _url.replace(`:${key}`, value.toString());
  }, url);
};

// one item always returned use get
// one item by search term returned find
export const eventStoreApiClient = (apiUrl: string) => {
  return {
    createPaginatedGet:
      <Entity extends any, Params extends RequestParams = RequestParams>(
        resource: string
      ) =>
      (
        params: Partial<PaginationParams<Entity>> & Params
      ): Observable<PaginatedResponse<Entity>> =>
        fromFetch(`${apiUrl}${replacePaginatedParams(resource, params)}`).pipe(
          fromFetchResponseHandler,
          catchError(globalErrorHandler)
        ),

    createRpcGet:
      <Entity extends any, Params extends RequestParams>(resource: string) =>
      (params: Params): Promise<Entity> => {
        return fetch(`${apiUrl}${replaceParams(resource, params)}`).then(
          fetchResponseHandler
        );
      },

    createGet:
      <Entity extends any, Params extends RequestParams>(resource: string) =>
      (params: Params): Observable<Entity> => {
        return fromFetch(`${apiUrl}${replaceParams(resource, params)}`).pipe(
          fromFetchResponseHandler,
          catchError(globalErrorHandler)
        );
      },

    createPaginatedPost:
      <Entity extends any, Params extends RequestParams = RequestParams>(
        resource: string
      ) =>
      ({
        page_size,
        order_by,
        order_direction,
        page,
        includes,
        ...params
      }: Partial<PaginationParams<Entity>> & Params): Observable<
        PaginatedResponse<Entity>
      > =>
        fromFetch(
          `${apiUrl}${replacePaginatedParams(resource, {
            page_size,
            order_by,
            order_direction,
            page,
            includes,
          })}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
          }
        ).pipe(fromFetchResponseHandler, catchError(globalErrorHandler)),
    createPost:
      <
        Entity extends any,
        Params extends PostRequestParams,
        PostQueryRequestParams extends QueryRequestParams = undefined,
      >(
        resource: string
      ) =>
      (
        params: Params,
        queryRequestParams?: PostQueryRequestParams
      ): Observable<Entity> => {
        const resourceQuery = queryRequestParams
          ? replaceParams(resource, queryRequestParams)
          : resource;
        return fromFetch(`${apiUrl}${resourceQuery}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        }).pipe(fromFetchResponseHandler, catchError(globalErrorHandler));
      },
  };
};

export const baseSubscription =
  <Entity extends any, Params extends PostRequestParams>(
    clientRequest: (params: Params) => Observable<Entity> | Promise<Entity>
  ): ((params: Params) => Observable<Entity>) =>
  (params: Params) =>
    from(clientRequest(params)).pipe(catchError(globalErrorHandler));

export const postBaseSubscription =
  <
    Entity extends any,
    Params extends PostRequestParams,
    PostQueryRequestParams = undefined,
  >(
    clientRequest: (
      params: Params,
      queryRequestParams?: PostQueryRequestParams
    ) => Observable<Entity> | Promise<Entity>
  ): ((
    params: Params,
    queryRequestParams?: PostQueryRequestParams
  ) => Observable<Entity>) =>
  (params: Params, queryRequestParams?: PostQueryRequestParams) =>
    from(clientRequest(params, queryRequestParams)).pipe(
      catchError(globalErrorHandler)
    );

export const getBatchSubscription =
  <Entity extends any, Params extends BatchGetRequestParams>(
    clientRequest: (
      params: BatchGetRequestParam
    ) => Observable<Entity> | Promise<Entity>
  ): ((params: Params) => Observable<Entity>) =>
  (params: Params) =>
    from(params).pipe(
      mergeMap((param) =>
        clientRequest({ batch_id: param }).pipe(catchError(globalErrorHandler))
      ),
      toArray()
    );

/**
 * Subscriptions
 */

/** BLOCK */

const getPaginatedBlocks = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<BlockResult>(`/blocks?includes=proposer_cspr_name`);
export const getPaginatedBlocksSubscription = pipe(
  baseSubscription(getPaginatedBlocks),
  map(MapPaginatedBlocks)
);

const getBlock = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<BlockResult>,
  { hash: string }
>(`/blocks/:hash?includes=proposer_cspr_name`);
export const getBlockSubscription = pipe(
  baseSubscription(getBlock),
  map(MapPaginatedBlock)
);

const getPaginatedBlockDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployResult, { hash: string; includes?: string }>(
  `/blocks/:hash/deploys`
);

export const getPaginatedBlockDeploysSubscription = pipe(
  baseSubscription(getPaginatedBlockDeploys),
  map(MapPaginatedDeploys)
);

const getBlockByHeight = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  any,
  { block_identifier: string }
>(`/blocks/:block_identifier`);
export const getBlockByHeightSubscription = baseSubscription(getBlockByHeight);

/** DEPLOY */

const getPaginatedDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployResult,
  {
    includes?: string;
    contract_package_hash?: string;
    contract_entrypoint_id?: string;
    caller_public_key?: string;
  }
>(`/deploys`);
export const getPaginatedDeploysSubscription = pipe(
  baseSubscription(getPaginatedDeploys),
  map(MapPaginatedDeploys)
);

const getDeploy = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<DeployResult>,
  {
    hash: string;
    includes?: string[];
  }
>(`/deploys/:hash`);
export const getDeploySubscription = pipe(
  baseSubscription(getDeploy),
  map(MapPaginatedDeploy)
);

const getPaginatedDeployTransfers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  {
    deploy_hash: string;
    includes?: string;
  }
>(`/deploys/:deploy_hash/transfers`);
export const getPaginatedDeployTransfersSubscription = baseSubscription(
  getPaginatedDeployTransfers
);

const getPaginatedDailyDeployNumbers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  Response<DailyDeployNumberResult>,
  { from: string; to: string; order_direction: string; order_by: string }
>(`/daily-deploy-numbers`);
export const getPaginatedDailyDeployNumbersSubscription = pipe(
  baseSubscription(getPaginatedDailyDeployNumbers),
  map(MapDailyDeployNumberResult)
);

const getDeployRawData = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<GetDeployResult>,
  { hash: string }
>(`/raw-transactions/:hash`);
export const getDeployRawDataSubscription = baseSubscription(getDeployRawData);

/** ACCOUNT */

const getPaginatedAccountDeploys = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployResult, { publicKey: string; includes?: string }>(
  `/accounts/:publicKey/deploys`
);
export const getPaginatedAccountDeploysSubscription = pipe(
  baseSubscription(getPaginatedAccountDeploys),
  map(MapPaginatedDeploys)
);

const getPaginatedAccountFeedTransactions = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DeployResult, { publicKey: string; includes?: string }>(
  `/accounts/:publicKey/feed-transactions`
);
export const getPaginatedAccountFeedTransactionsSubscription = pipe(
  baseSubscription(getPaginatedAccountFeedTransactions),
  map(MapPaginatedDeploys)
);

const getPaginatedAccountTransfers = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  {
    hash: string;
    from_hash?: string;
    to_hash?: string;
    from_purse?: string;
    to_purse?: string;
    includes?: string;
  }
>(`/accounts/:hash/transfers`);
export const getPaginatedAccountTransfersSubscription = baseSubscription(
  getPaginatedAccountTransfers
);

const getPaginatedAccountDelegations = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorResult,
  {
    publicKey: string;
    includes?: string;
    page_size?: number;
  }
>(`/accounts/:publicKey/delegations`);
export const getPaginatedAccountDelegationsSubscription = baseSubscription(
  getPaginatedAccountDelegations
);

const getPaginatedAccountTokens = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenResult,
  { hash: string; includes?: string; is_burned?: boolean }
>(`/accounts/:hash/nft-tokens`);
export const getPaginatedAccountTokensSubscription = baseSubscription(
  getPaginatedAccountTokens
);

const getPaginatedAccountNftCollections = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenOwnerResult,
  { accountHash: string; includes?: string }
>(`/accounts/:accountHash/nft-token-ownership`);
export const getPaginatedAccountNftCollectionsSubscription = baseSubscription(
  getPaginatedAccountNftCollections
);

const getPaginatedAccountDelegatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorRewardResult,
  { publicKey: string; includes?: string }
>(`/accounts/:publicKey/delegation-rewards`);
export const getPaginatedAccountDelegatorRewardsSubscription = baseSubscription(
  getPaginatedAccountDelegatorRewards
);

const getPaginatedAccountDelegatorRewardsByEra = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorRewardByEraResult,
  { publicKey: string; includes?: string }
>(`/accounts/:publicKey/era-delegation-rewards`);
export const getPaginatedAccountDelegatorRewardsByEraSubscription =
  baseSubscription(getPaginatedAccountDelegatorRewardsByEra);

const getAccountsPublicKey = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AccountBalanceInfoResult,
  { account_hash?: string; main_purse_uref?: string; includes?: string }
>(`/accounts`);
export const getAccountsPublicKeySubscription =
  baseSubscription(getAccountsPublicKey);

const getPaginatedAccountInfo = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AccountInfoResult,
  { account_hash?: string; main_purse_uref?: string; search?: string }
>(`/account-info`);
export const getPaginatedAccountInfoSubscription = baseSubscription(
  getPaginatedAccountInfo
);

const getCentralizedAccountInfo = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<AccountCentralizedInfo[]>,
  { account_hash?: string; search?: string }
>(`/centralized-account-info`);
export const getCentralizedAccountInfoSubscription = baseSubscription(
  getCentralizedAccountInfo
);

const getAccountInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<AccountInfoResult>,
  { accountHash: string; includes?: string }
>(`/accounts/:accountHash`);
export const getAccountInfoSubscription = baseSubscription(getAccountInfo);

const postAccounts = eventStoreApiClient(CSPR_LIVE_API_URL).createPaginatedPost<
  AccountInfoResult,
  { account_hashes?: string[]; includes?: string }
>(`/accounts`);
export const postAccountSubscription = baseSubscription(postAccounts);

const getAccountBalanceByPublicKey = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<AccountBalanceResult, { publicKey: string; includes?: string }>(
  `/accounts/:publicKey`
);
export const getAccountBalanceByPublicKeySubscription = baseSubscription(
  getAccountBalanceByPublicKey
);

const getAccountContractPackages = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<DeployContractPackageResult[]>,
  { publicKey: string; includes?: string }
>(`/accounts/:publicKey/contract-packages`);
export const getAccountContractPackagesSubscription = baseSubscription(
  getAccountContractPackages
);

/** CEP-18 **/
const getPaginatedAccountCep18TokenList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTBalanceTokenResult,
  { accountHash: string; includes?: string }
>(`/accounts/:accountHash/ft-token-ownership`);
export const getPaginatedAccountCep18TokenListSubscription = baseSubscription(
  getPaginatedAccountCep18TokenList
);

const getPaginatedContractsCep18TokenList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTBalanceTokenResult,
  { owner_hash: string; includes?: string }
>(`/ft-token-ownerships`);
export const getPaginatedContractsCep18TokenListSubscription = baseSubscription(
  getPaginatedContractsCep18TokenList
);

const getContractPackageCep18TokenList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTokenOwnerResult,
  { contractPackageHash: string; includes?: string }
>(`/contract-packages/:contractPackageHash/ft-token-ownership`);
export const getContractPackageCep18TokenListSubscription = baseSubscription(
  getContractPackageCep18TokenList
);

const getPaginatedAccountTokensTxsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTransactionResult,
  {
    accountHash: string;
    includes?: string;
  }
>(`/accounts/:accountHash/ft-token-actions`);
export const getPaginatedAccountTokensTxsListSubscription = baseSubscription(
  getPaginatedAccountTokensTxsList
);

const getPaginatedContractPackageIssuedTokensTxsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTransactionResult,
  {
    contractPackageHash: string;
    includes?: string;
  }
>(`/contract-packages/:contractPackageHash/ft-token-actions`);
export const getPaginatedContractPackageIssuedTokensTxsListSubscription =
  baseSubscription(getPaginatedContractPackageIssuedTokensTxsList);

const getPaginatedContractPackageInvolvedTokensTxsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  FTTransactionResult,
  {
    owner_hash: string;
    includes?: string;
  }
>(`/ft-token-actions`);
export const getPaginatedContractPackageInvolvedTokensTxsListSubscription =
  baseSubscription(getPaginatedContractPackageInvolvedTokensTxsList);

const getPaginatedPendingUndelegationsList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AccountPendingUndelegation,
  {
    publicKey: string;
    includes?: string;
  }
>(`/accounts/:publicKey/undelegations`);
export const getPaginatedPendingUndelegationsListSubscription =
  baseSubscription(getPaginatedPendingUndelegationsList);

/** VALIDATOR **/

const getAuctionMetrics = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ValidatorMetricsResult>,
  Record<string, never>
>(`/auction-metrics`);
export const getValidatorAuctionMetricsSubscription =
  baseSubscription(getAuctionMetrics);

const getPaginatedValidatorsData = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorResult,
  { era_id: number; includes?: string; page_size?: number; is_active?: string }
>(`/validators`);

export const getPaginatedValidatorEraDataSubscription = pipe(
  baseSubscription(getPaginatedValidatorsData),
  map(MapPaginatedValidators)
);

const getPaginatedValidatorAuctionData = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorResult,
  { era_id: number; includes?: string; page_size?: number; is_active?: string }
>(`/bidders`);
export const getPaginatedValidatorAuctionDataSubscription = pipe(
  baseSubscription(getPaginatedValidatorAuctionData),
  map(MapPaginatedValidators)
);

const getPaginatedValidatorBlocks = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<BlockResult, { publicKeyHex: string }>(
  `/validators/:publicKeyHex/blocks`
);
export const getPaginatedValidatorBlocksSubscription = pipe(
  baseSubscription(getPaginatedValidatorBlocks),
  map(MapPaginatedBlocks)
);

const getPaginatedValidatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ValidatorRewardResult,
  { publicKeyHex: string; includes?: string }
>(`/validators/:publicKeyHex/era-rewards`);
export const getPaginatedValidatorRewardsSubscription = baseSubscription(
  getPaginatedValidatorRewards
);

const getValidatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/validators/:publicKey/total-rewards`
);
export const getValidatorsTotalRewardsSubscription = baseSubscription(
  getValidatorsTotalRewards
);

const getValidatorsTotalDelegatorRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/validators/:publicKey/total-delegator-rewards`
);
export const getValidatorsTotalDelegatorRewardsSubscription = baseSubscription(
  getValidatorsTotalDelegatorRewards
);

const getValidatorsDelegations = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<DelegatorResult, { publicKey: string; includes?: string }>(
  `/validators/:publicKey/delegations`
);
export const getValidatorsDelegationsSubscription = baseSubscription(
  getValidatorsDelegations
);

const getValidatorsUnstakingBalance = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<ValidatorsUnstakingBalanceResult>,
  { publicKey: string; eraId: number }
>(`/validators/:publicKey/unstaking-balance/:eraId`);
export const getValidatorsUnstakingBalanceSubscription = baseSubscription(
  getValidatorsUnstakingBalance
);

/** BIDDER **/

const getBidderDetailsData = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ValidatorResult>,
  { publicKey: string; era_id: number; includes?: string; is_active?: string }
>(`/bidders/:publicKey`);

export const getBidderDetailsDataSubscription = pipe(
  baseSubscription(getBidderDetailsData),
  map(MapDataResponseValidators)
);

/** DELEGATOR **/

const getDelegatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<TotalRewardsResult>, { publicKey: string }>(
  `/accounts/:publicKey/total-delegation-rewards`
);
export const getDelegatorsTotalRewardsSubscription = baseSubscription(
  getDelegatorsTotalRewards
);

/** CURRECY RATES */

const getCurrencyRates = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<CurrencyRateResult>,
  { currencyCode: string }
>(`/rates/:currencyCode/amount`);
export const getCurrencyRateSubscription = baseSubscription(getCurrencyRates);

/** UNISWAP PAIRS */

const getFriendlyMarketTokenPair = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<FriendlyMarketTokenPairResult>, BatchGetRequestParam>(
  `/uniswap-v2-pairs/:batch_id`
);

export const getFriendlyMarketTokenPairBatchSubscription = getBatchSubscription(
  getFriendlyMarketTokenPair
);

/** SUPPLY */

const getSupply = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<SupplyResult>,
  Record<string, never>
>(`/supply?is_cached=1`);
export const getSupplySubscription = baseSubscription(getSupply);

/** STATUS */

const getStatus = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<StatusResult>,
  Record<string, never>
>(`/network-state`);
export const getStatusSubscription = baseSubscription(getStatus);

/** PROXY
 * Those methods are proxies for RPC calls and returning cached data.
 * **/

export const rpcStateGetItem = eventStoreApiClient(RPC_URL).createRpcGet<
  RpcResponse<{ stored_value: any }>,
  {
    state_root_hash: string;
    key: string;
  }
>(`/state_get_item`);

export const rpcStateGetBalance = eventStoreApiClient(RPC_URL).createRpcGet<
  RpcResponse<{ balance_value: string }>,
  { state_root_hash: string; purse_uref: string }
>(`/state_get_balance`);

export const rpcSpeculativeExecDeploy = eventStoreApiClient(
  SPECULATIVE_EXEC_RPC_URL
).createPost<
  RpcResponse<any>,
  {
    jsonrpc: string;
    id: string;
    method: string;
    params: any;
  }
>('');

/** CONTRACT */
const getPaginatedContractPackages = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployContractPackageResult,
  {
    includes?: string;
    contract_package_hash?: string;
    owner_hash?: string;
    is_featured?: boolean;
    search?: string;
  }
>(`/contract-packages`);
export const getPaginatedContractPackagesSubscription = baseSubscription(
  getPaginatedContractPackages
);

const getContractPackageInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<DeployContractPackageResult> | null,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash`);
export const getContractPackageInfoSubscription = baseSubscription(
  getContractPackageInfo
);

const getContractPackageContractsInfo = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DataResponse<ContractPackageVersion> | null,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash/contracts`);
export const getContractPackageContractsInfoSubscription = baseSubscription(
  getContractPackageContractsInfo
);

const getPaginatedContractPackageFeedTransactions = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployResult,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash/feed-transactions`);

export const getPaginatedContractPackageFeedTransactionsSubscription = pipe(
  baseSubscription(getPaginatedContractPackageFeedTransactions),
  map(MapPaginatedDeploys)
);

const getPaginatedContractPackageTokens = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenResult,
  { contract_package_hash: string; includes?: string; is_burned?: boolean }
>(`/contract-packages/:contract_package_hash/nft-tokens`);

export const getPaginatedContractPackageTokensSubscription = baseSubscription(
  getPaginatedContractPackageTokens
);

const useGetContractNftToken = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<NFTTokenResult> & { metadata: NFTTokenMetadataEntry[] },
  { contract_package_hash: string; token_id: string; includes?: string }
>(`/contract-packages/:contract_package_hash/nft-tokens/:token_id`);
export const useGetContractNftTokenSubscription = baseSubscription(
  useGetContractNftToken
);

const useGetPaginatedNftTokenActivity = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenActivityResult,
  { contract_package_hash: string; token_id: string; includes?: string }
>(
  `/contract-packages/:contract_package_hash/nft-tokens/:token_id/nft-token-actions`
);
export const getPaginatedNftTokenActivitySubscription = baseSubscription(
  useGetPaginatedNftTokenActivity
);

const getContractInfo = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<ContractResult> | null,
  { contract_hash: string; includes?: string }
>(`/contracts/:contract_hash`);
export const getContractInfoSubscription = baseSubscription(getContractInfo);

const getContractEntryPoints = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployEntryPointResult,
  { contract_hash: string; includes?: string }
>(`/contracts/:contract_hash/entry-points`);
export const getContractEntryPointsSubscription = baseSubscription(
  getContractEntryPoints
);

const getContractEntryPointCosts = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<ContractEntryPointCostsResult>,
  { contract_hash: string; entry_point_name: string }
>(`/contracts/:contract_hash/entry-points/:entry_point_name/costs`);
export const getContractEntryPointCostsSubscription = baseSubscription(
  getContractEntryPointCosts
);

const getPaginatedContractPackageEntryPoints = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DeployEntryPointResult,
  { contract_package_hash: string; includes?: string }
>(`/contract-entry-points`);
export const getPaginatedContractPackageEntryPointsSubscription =
  baseSubscription(getPaginatedContractPackageEntryPoints);

const getPaginatedContractPackageNftOwners = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenOwnerResult,
  { contract_package_hash: string; includes?: string }
>(`/contract-packages/:contract_package_hash/nft-token-ownership`);

export const getPaginatedContractPackageNftOwnersSubscriptions =
  baseSubscription(getPaginatedContractPackageNftOwners);

const getPaginatedContractPackageOwnedNfts = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  NFTTokenResult,
  {
    owner_hash: string;
    includes?: string;
    is_burned?: boolean;
    contract_package_hash?: string;
  }
>(`/nft-tokens`);

export const getPaginatedContractPackageOwnedNftsSubscriptions =
  baseSubscription(getPaginatedContractPackageOwnedNfts);

/** CURRENCY */
const getAvailableCurrencyList = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<CurrencyFiatResult[]>, Record<string, never>>(
  `/currencies`
);
export const getAvailableCurrencyListSubscription = baseSubscription(
  getAvailableCurrencyList
);

/** ACTIVITY EXPORT */

const getPaginatedAccountTokensByContractPackageHash = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  ActivityExportEntitlementNFTEntryType,
  { accountHash: string; contractPackageHash: string; includes?: string }
>(
  `/accounts/:accountHash/nft-tokens?contract_package_hash=:contractPackageHash`
);
export const getPaginatedAccountTokensByContractPackageHashSubscription = pipe(
  baseSubscription(getPaginatedAccountTokensByContractPackageHash),
  map(MapTokensMetadataAsList),
  map(MapMetadata)
);

const getPaginatedActivityExportRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<ActivityExportRecentRequest, { publicKey: string }>(
  `/accounts/:publicKey/activity-requests`
);
export const getPaginatedActivityExportRequestSubscription = baseSubscription(
  getPaginatedActivityExportRequest
);

const postActivityExportRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse<ActivityExportRequestResult>,
  ActivityExportRequestType,
  { publicKey: string }
>(`/accounts/:publicKey/activity-requests`);
export const postActivityExportRequestSubscription = postBaseSubscription(
  postActivityExportRequest
);

const createActivityExportDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  RawDeployJSON,
  {
    account_public_key: string;
    years: number[];
  },
  {
    gas_price_tolerance: number;
  }
>(`/create-account-export-entitlement-deploy`);

export const createActivityExportDeployRequestSubscription =
  postBaseSubscription(createActivityExportDeployRequest);

const createActivityExportTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  RawTransactionJSON,
  {
    account_public_key: string;
    years: number[];
  },
  {
    gas_price_tolerance: number;
  }
>(`/create-account-export-entitlement-transaction`);
export const createActivityExportTransactionRequestSubscription =
  postBaseSubscription(createActivityExportTransactionRequest);

const submitActivityExportDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse<ActivityExportRequestResult>,
  {
    deploy: RawDeployJSON;
  },
  Record<string, never>
>(`/submit-account-export-entitlement-deploy`);
export const submitActivityExportDeployRequestSubscription =
  postBaseSubscription(submitActivityExportDeployRequest);

const submitActivityExportTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse<ActivityExportRequestResult>,
  {
    transaction: RawTransactionJSON;
  },
  Record<string, never>
>(`/submit-account-export-entitlement-transaction`);
export const submitActivityExportTransactionRequestSubscription =
  postBaseSubscription(submitActivityExportTransactionRequest);

const getAccountActivityDeployStatuses = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<
  DataResponse<ActivityExportDeployStatusesResult>,
  Record<string, never>
>(`/account-activity-export-statuses`);
export const getAccountActivityDeployStatusesSubscription = baseSubscription(
  getAccountActivityDeployStatuses
);

/** ANALYTICS */

const getNetworkStats = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<NetworkStats> | null,
  Record<string, never>
>(`/network-stats`);
export const getNetworkStatsSubscription = baseSubscription(getNetworkStats);

const getRatesHistory = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  any,
  {
    from: string;
    to: string;
    currency_id: number;
    order_direction: string;
    order_by: string;
  }
>(`/currencies/:currency_id/daily-rates`);
export const getRatesHistorySubscription = baseSubscription(getRatesHistory);

const getFtRatesHistory = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  PaginatedResponse<FTTokenRatesHistoryResult>,
  {
    contract_package_hash: string;
    from: string;
    to: string;
    currency_id: number;
    dex_id?: number;
  }
>(`/ft/:contract_package_hash/daily-rates`);
export const getFtRatesHistorySubscription =
  baseSubscription(getFtRatesHistory);

/** MULTISIG 1.5 */

const submitMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    recaptcha_client_token: string;
    deploy: any;
  },
  Record<string, never>
>(`/multisig-deploys`);
export const submitMultisigDeployRequestSubscription = postBaseSubscription(
  submitMultisigDeployRequest
);

const addSignatureToMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    signer: string;
    signature: string;
  },
  { transaction_hash: string }
>(`/multisig-deploys/:transaction_hash/approvals`);
export const addSignatureToMultisigDeployRequestSubscription =
  postBaseSubscription(addSignatureToMultisigDeployRequest);

const getMultisigDeployRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<RawDeployJSON>, { deploy_hash: string }>(
  `/multisig-deploys/:transaction_hash`
);
export const getMultisigDeployRequestSubscription = baseSubscription(
  getMultisigDeployRequest
);

/** MULTISIG 2.0 */

const submitMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    recaptcha_client_token: string;
    transaction: any;
  },
  Record<string, never>
>(`/multisig-transactions`);
export const submitMultisigTransactionRequestSubscription =
  postBaseSubscription(submitMultisigTransactionRequest);

const addSignatureToMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPost<
  DataResponse,
  {
    signer: string;
    signature: string;
  },
  { transaction_hash: string }
>(`/multisig-transactions/:transaction_hash/approvals`);
export const addSignatureToMultisigTransactionRequestSubscription =
  postBaseSubscription(addSignatureToMultisigTransactionRequest);

const getMultisigTransactionRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<RawTransactionJSON>, { transactionHash: string }>(
  `/multisig-transactions/:transactionHash`
);
export const getMultisigTransactionRequestSubscription = baseSubscription(
  getMultisigTransactionRequest
);

const getKeysToWhichAccountAssociated = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AssociatedKeyRelatedToAccount,
  { accountHash: string; page_size?: number; includes?: string }
>(`/accounts/:accountHash/associated-to-accounts`);
export const getKeysToWhichAccountAssociatedSubscription = baseSubscription(
  getKeysToWhichAccountAssociated
);

/** ADS */
const adsContactRequest = eventStoreApiClient(CSPR_LIVE_API_URL).createPost<
  DataResponse,
  {
    company_or_project_name: string;
    company_or_project_url: string;
    contact_email: string;
    contact_name: string;
    message: string;
    recaptcha_client_token: string;
  },
  Record<string, never>
>(`/ads-contact-requests`);
export const adsContactRequestSubscription =
  postBaseSubscription(adsContactRequest);

/** UREF */

const getUrefTransfersRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  TransferResult,
  { purseUref: string; includes?: string; limit?: number }
>(`/purses/:purseUref/transfers`);
export const getUrefTransfersRequestSubscription = baseSubscription(
  getUrefTransfersRequest
);

const getUrefDelegationsRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  URefDelegationResult,
  { urefKey: string; includes?: string }
>(`/urefs/:urefKey/delegations`);
export const getPaginatedUrefDelegationsRequestSubscription = baseSubscription(
  getUrefDelegationsRequest
);

const getUrefRewardsRequest = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  DelegatorRewardResult,
  { urefKey: string; includes?: string }
>(`/urefs/:urefKey/delegation-rewards`);
export const getPaginatedUrefRewardsRequestSubscription = baseSubscription(
  getUrefRewardsRequest
);

const getUrefDelegatorsTotalRewards = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<string>, { uref_key: string }>(
  `/urefs/:uref_key/total-delegation-rewards`
);
export const getUrefDelegatorsTotalRewardsSubscription = baseSubscription(
  getUrefDelegatorsTotalRewards
);

const getUrefUndelegationBalance = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<any>, { uref_key: string; era_id: number }>(
  `/urefs/:uref_key/undelegation-balance/:era_id`
);
export const getUrefUndelegationBalanceSubscription = baseSubscription(
  getUrefUndelegationBalance
);

/** CSPR NAME */

const getCsprNameInfoRequest = eventStoreApiClient(CSPR_LIVE_API_URL).createGet<
  DataResponse<CsprNameInfoResult>,
  { name: string; includes?: string }
>(`/cspr-name-resolutions/:name`);
export const getCsprNameInfoRequestSubscription = baseSubscription(
  getCsprNameInfoRequest
);

/** INFLATION */

const getTotalEcosystemSustainIssuances = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createGet<DataResponse<number>, { purse_uref: string; includes?: string }>(
  `/ecosystem-sustain-issuances/total`
);
export const getTotalEcosystemSustainIssuancesSubscription = baseSubscription(
  getTotalEcosystemSustainIssuances
);

const getEcosystemSustainIssuances = eventStoreApiClient(
  CSPR_LIVE_API_URL
).createPaginatedGet<
  AllocationHistoryResult,
  { purse_uref: string; includes?: string }
>(`/ecosystem-sustain-issuances`);
export const getEcosystemSustainIssuancesSubscription = baseSubscription(
  getEcosystemSustainIssuances
);
